/**
 * Bounded in-process TTL cache with request coalescing.
 *
 * For server-side reads whose upstream is slow or metered and whose answer is
 * the same for every visitor. The app runs as a long-lived Node server
 * (`next start`), so entries survive between requests.
 *
 * Deliberately separate from the cache inside `lib/seo/catalog.ts`, which keeps
 * different semantics: that one holds one big read model for an hour, serves it
 * stale while revalidating, and caches misses so a root catch-all cannot be
 * used to hammer upstream. Callers here want the opposite — a short TTL and no
 * caching of failures — so the two are not one knob apart.
 */

type Entry = { value: unknown; expires: number };

type Buckets = {
  memo: Map<string, Entry>;
  pending: Map<string, Promise<unknown>>;
};

export interface ServerCache {
  /**
   * Resolve `key`, calling `load` only on a miss. Concurrent callers for the
   * same key share one in-flight `load`.
   *
   * `store` decides whether a produced value is worth keeping, so a caller can
   * refuse to cache failures or absences. It defaults to keeping everything.
   */
  read<T>(
    key: string,
    load: () => Promise<T>,
    store?: (value: T) => boolean
  ): Promise<T>;
}

export function createServerCache(
  namespace: string,
  { ttlMs, maxEntries = 1024 }: { ttlMs: number; maxEntries?: number }
): ServerCache {
  // Next bundles route handlers, layouts and instrumentation independently, so
  // a module-local map would give each bundle its own copy and cache nothing
  // that another bundle reads. The Node process global does cross those
  // boundaries.
  const store = globalThis as typeof globalThis & {
    __drepServerCaches?: Map<string, Buckets>;
  };
  const caches = (store.__drepServerCaches ??= new Map<string, Buckets>());
  let cache = caches.get(namespace);
  if (!cache) {
    cache = { memo: new Map<string, Entry>(), pending: new Map() };
    caches.set(namespace, cache);
  }
  const { memo, pending } = cache;

  function remember(key: string, value: unknown) {
    if (memo.size >= maxEntries) {
      const now = Date.now();
      // forEach rather than for..of: the tsconfig target predates iterators.
      const stale: string[] = [];
      memo.forEach((entry, candidate) => {
        if (entry.expires <= now) stale.push(candidate);
      });
      stale.forEach((candidate) => memo.delete(candidate));
      // Still full: everything is live, so evict from the oldest end. Map
      // preserves insertion order, so an approximate LRU is enough.
      while (memo.size >= maxEntries) {
        const oldest = memo.keys().next();
        if (oldest.done) break;
        memo.delete(oldest.value);
      }
    }
    memo.delete(key);
    memo.set(key, { value, expires: Date.now() + ttlMs });
  }

  return {
    read<T>(
      key: string,
      load: () => Promise<T>,
      shouldStore?: (value: T) => boolean
    ): Promise<T> {
      const hit = memo.get(key);
      if (hit && hit.expires > Date.now())
        return Promise.resolve(hit.value as T);
      // An expired entry is useless to us: callers here would rather wait for
      // a fresh answer than be handed a stale one.
      if (hit) memo.delete(key);

      const inFlight = pending.get(key) as Promise<T> | undefined;
      if (inFlight) return inFlight;

      const request = Promise.resolve()
        .then(load)
        .then((value) => {
          if (!shouldStore || shouldStore(value)) remember(key, value);
          return value;
        })
        .finally(() => pending.delete(key));
      pending.set(key, request);
      return request;
    },
  };
}
